import React from "react";
import Slide from "@mui/material/Slide";
import { TransitionProps } from "@mui/material/transitions";
import {
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    FormControl,
    InputLabel,
    MenuItem,
    ListItemText,
    Select,
    SelectChangeEvent,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import CustomImage from "../../components/CustomImage";
import ImageUpload from "../../components/ImageUploader";
import { ExternalCatalogRow } from "../../types/externalCatalog";
import { Camp } from "../../types/camps";

type PricingMode = 'markupAmount' | 'markupPercentage';

const Transition = React.forwardRef(function Transition(
    props: TransitionProps & {
        children: React.ReactElement<any, any>;
    },
    ref: React.Ref<unknown>
) {
    return <Slide direction="up" ref={ref} {...props} />;
});

interface Props {
    open: boolean;
    row: ExternalCatalogRow | null;
    camps?: Camp[];
    saving?: boolean;
    corporateScope?: boolean;
    showBookingDefaults?: boolean;
    showTerms?: boolean;
    onClose: () => void;
    onSubmit: (value: {
        termsAndConditions?: string;
        name?: string;
        pricingMode?: PricingMode;
        customPrice?: number | null;
        customMrp?: number | null;
        markupPercentage?: number | null;
        markupAmount?: number | null;
        imageFile?: File | null;
        coverImageFile?: File | null;
        existingImage?: string;
        existingCoverImage?: string;
        pinned?: boolean;
        enabled?: boolean;
        gender?: 'male' | 'female' | '';
        applicableAge?: number | null;
        campId?: string;
        campIds?: string[];
        skipReport?: boolean;
        skipHealthCertificate?: boolean;
        reportEscalationHours?: number | null;
    }) => Promise<void>;
}

const EditExternalCatalogDialog: React.FC<Props> = ({
    open,
    row,
    camps = [],
    saving = false,
    corporateScope = false,
    showBookingDefaults = false,
    showTerms = false,
    onClose,
    onSubmit,
}) => {
    const [termsAndConditions, setTermsAndConditions] = React.useState('');
    const [name, setName] = React.useState('');
    const [pricingMode, setPricingMode] = React.useState<PricingMode>('markupPercentage');
    const [markupPercentage, setMarkupPercentage] = React.useState('');
    const [markupAmount, setMarkupAmount] = React.useState('');
    const [imageFile, setImageFile] = React.useState<File | null>(null);
    const [coverImageFile, setCoverImageFile] = React.useState<File | null>(null);
    const [existingImage, setExistingImage] = React.useState('');
    const [existingCoverImage, setExistingCoverImage] = React.useState('');
    const [pinned, setPinned] = React.useState(false);
    const [enabled, setEnabled] = React.useState(false);
    const [gender, setGender] = React.useState<'male' | 'female' | ''>('');
    const [applicableAge, setApplicableAge] = React.useState('');
    const [campIds, setCampIds] = React.useState<string[]>([]);
    const [skipReport, setSkipReport] = React.useState(false);
    const [requireHealthCertificate, setRequireHealthCertificate] = React.useState(false);
    const [reportEscalationHours, setReportEscalationHours] = React.useState('');
    const showReportEscalation = showTerms && row?.itemType === 'package';

    React.useEffect(() => {
        const source = corporateScope ? row?.catalogAssignmentOverrides : row;
        setName(source?.name || '');
        setTermsAndConditions(row?.termsAndConditions || '');
        setReportEscalationHours(
            typeof row?.reportEscalationHours === 'number' ? String(row.reportEscalationHours) : ''
        );
        setMarkupPercentage(
            typeof source?.markupPercentage === 'number'
                ? String(source.markupPercentage)
                : ''
        );
        setMarkupAmount(
            typeof source?.markupAmount === 'number'
                ? String(source.markupAmount)
                : ''
        );
        setPricingMode(
            typeof source?.markupAmount === 'number' && source.markupAmount > 0
                ? 'markupAmount'
                : 'markupPercentage'
        );
        setExistingImage(source?.image || '');
        setExistingCoverImage(source?.coverImage || '');
        setImageFile(null);
        setCoverImageFile(null);
        setPinned(Boolean(source?.pinned));
        setEnabled(Boolean(row?.enabled));
        setSkipReport(source?.skipReport === true);
        setRequireHealthCertificate(source?.skipHealthCertificate === false);
        setGender(source?.gender || '');
        setApplicableAge(
            typeof source?.applicableAge === 'number'
                ? String(source.applicableAge)
                : ''
        );
        const nextCampIds = source?.campIds || [];
        setCampIds(
            nextCampIds.length
                ? nextCampIds
                : source?.campId
                    ? [source.campId]
                    : []
        );
    }, [row, corporateScope]);

    const handleCampChange = (event: SelectChangeEvent<string[]>) => {
        const value = event.target.value;
        setCampIds(typeof value === 'string' ? value.split(',') : value);
    };

    const handleSubmit = async () => {
        // The backend computes the final price from the provider's base price
        // and stores it in customPrice. Only one markup knob is active at a time.
        // Clearing the markup field removes both knobs and the computed price.
        const markupValue =
            pricingMode === 'markupPercentage'
                ? markupPercentage.trim()
                : markupAmount.trim();
        const markupCleared = !markupValue;

        await onSubmit({
            termsAndConditions: showTerms && row?.itemType === 'package' ? termsAndConditions.trim() : undefined,
            name: name.trim() || undefined,
            pricingMode,
            customPrice: markupCleared ? null : undefined,
            customMrp: markupCleared ? null : undefined,
            markupPercentage:
                pricingMode === 'markupPercentage' && markupValue
                    ? Number(markupValue)
                    : pricingMode === 'markupAmount' && !markupCleared
                        ? 0
                        : null,
            markupAmount:
                pricingMode === 'markupAmount' && markupValue
                    ? Number(markupValue)
                    : pricingMode === 'markupPercentage' && !markupCleared
                        ? 0
                        : null,
            imageFile,
            coverImageFile,
            existingImage,
            existingCoverImage,
            pinned,
            enabled,
            gender,
            applicableAge: row?.itemType === 'package'
                ? applicableAge.trim()
                    ? Number(applicableAge)
                    : null
                : undefined,
            campId: campIds[0],
            campIds,
            reportEscalationHours: showReportEscalation
                ? reportEscalationHours.trim() ? Number(reportEscalationHours) : null
                : undefined,
            skipReport: showBookingDefaults && row?.itemType === 'package' ? skipReport : undefined,
            skipHealthCertificate: showBookingDefaults && row?.itemType === 'package'
                ? !requireHealthCertificate
                : undefined,
        });
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            TransitionComponent={Transition}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle>
                Edit {row?.itemType === 'test' ? 'Test' : 'Package'} Metadata
            </DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2}>
                    <Typography variant="body2" color="text.secondary">
                        External ID: {row?.externalItemId || '-'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Provider Name: {row?.providerName || '-'}
                    </Typography>
                    <TextField
                        label="Display Name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        fullWidth
                        helperText={corporateScope ? 'Leave blank to inherit the public name.' : undefined}
                    />
                    {showTerms && row?.itemType === 'package' && (
                        <TextField
                            label="Terms and Conditions"
                            value={termsAndConditions}
                            onChange={(e) => setTermsAndConditions(e.target.value)}
                            multiline
                            minRows={6}
                            fullWidth
                            helperText="Used in booking emails. Leave blank to use vendor terms; if neither has terms, no link is shown."
                        />
                    )}
                    {showReportEscalation && (
                        <TextField
                            label="Report escalation after (hours)"
                            type="number"
                            inputProps={{ min: 1, max: 240, step: 1 }}
                            value={reportEscalationHours}
                            onChange={(e) => setReportEscalationHours(e.target.value)}
                            fullWidth
                            helperText="Hours after the visit before the lab and support are emailed about a missing report. Leave blank for 48 hours; up to 240. Applies to new bookings."
                        />
                    )}
                    {row?.itemType === 'package' ? (
                        <>
                            {showBookingDefaults ? (
                                <Stack>
                                    <Typography variant="subtitle2">Defaults for new bookings</Typography>
                                    <FormControlLabel
                                        control={<Checkbox checked={skipReport} onChange={(e) => setSkipReport(e.target.checked)} />}
                                        label="Skip report"
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={requireHealthCertificate}
                                                onChange={(e) => setRequireHealthCertificate(e.target.checked)}
                                            />
                                        }
                                        label="Require health/fitness certificate"
                                    />
                                    <Typography variant="caption" color="text.secondary">
                                        These settings are copied to a booking when it is created; Booking Details can change them later.
                                    </Typography>
                                </Stack>
                            ) : null}
                            <FormControl fullWidth>
                                <InputLabel>Camps</InputLabel>
                                <Select
                                    multiple
                                    label="Camps"
                                    value={campIds}
                                    onChange={handleCampChange}
                                    renderValue={(selected) =>
                                        selected.length
                                            ? selected.join(', ')
                                            : 'No camps'
                                    }
                                >
                                    {camps.map((camp) => (
                                        <MenuItem key={camp.campLsId} value={camp.campLsId}>
                                            <Checkbox checked={campIds.includes(camp.campLsId)} />
                                            <ListItemText
                                                primary={`${camp.campLsId}${camp.corporateName ? ` - ${camp.corporateName}` : ''}`}
                                            />
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl fullWidth>
                                <InputLabel>Gender applicability</InputLabel>
                                <Select
                                    label="Gender applicability"
                                    value={gender}
                                    onChange={(e) => setGender(e.target.value as 'male' | 'female' | '')}
                                >
                                    <MenuItem value="">Not gender specific</MenuItem>
                                    <MenuItem value="male">Male</MenuItem>
                                    <MenuItem value="female">Female</MenuItem>
                                </Select>
                            </FormControl>
                            <TextField
                                label="Applicable from age"
                                type="number"
                                value={applicableAge}
                                onChange={(e) => setApplicableAge(e.target.value)}
                                inputProps={{ min: 0, max: 120, step: 1 }}
                                helperText="Leave blank for all ages. Enter 40 for 40 years and above."
                                fullWidth
                            />
                        </>
                    ) : null}
                    <FormControl fullWidth>
                        <InputLabel>Pricing Method</InputLabel>
                        <Select
                            label="Pricing Method"
                            value={pricingMode}
                            onChange={(e) => setPricingMode(e.target.value as PricingMode)}
                        >
                            <MenuItem value="markupPercentage">Markup Percentage</MenuItem>
                            <MenuItem value="markupAmount">Markup Amount</MenuItem>
                        </Select>
                    </FormControl>
                    {pricingMode === 'markupAmount' ? (
                        <TextField
                            label="Markup Amount (Rs.)"
                            type="number"
                            value={markupAmount}
                            onChange={(e) => setMarkupAmount(e.target.value)}
                            inputProps={{ min: 0, step: 0.01 }}
                            helperText={
                                typeof row?.price === 'number'
                                    ? `Final price = Rs. ${row.price} (provider price) + this amount, stored as custom price.`
                                    : 'Added on top of the provider base price and stored as custom price.'
                            }
                            fullWidth
                        />
                    ) : (
                        <TextField
                            label="Markup Percentage"
                            type="number"
                            value={markupPercentage}
                            onChange={(e) => setMarkupPercentage(e.target.value)}
                            inputProps={{ min: 0, max: 100, step: 0.01 }}
                            helperText={
                                typeof row?.price === 'number'
                                    ? `Final price = Rs. ${row.price} (provider price) + this %, stored as custom price.`
                                    : 'Applied on top of the provider base price and stored as custom price.'
                            }
                            fullWidth
                        />
                    )}
                    <Stack direction="row" spacing={2}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={pinned}
                                    onChange={(e) => setPinned(e.target.checked)}
                                />
                            }
                            label="Pinned"
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={enabled}
                                    onChange={(e) => setEnabled(e.target.checked)}
                                />
                            }
                            label="Enabled"
                        />
                    </Stack>
                    <Stack direction="row" spacing={2}>
                        <Stack spacing={1} sx={{ flex: 1 }}>
                            <Typography variant="subtitle2">Image</Typography>
                            {existingImage ? (
                                <CustomImage
                                    src={existingImage}
                                    style={{ width: 160, height: 96, objectFit: 'cover', borderRadius: 8 }}
                                />
                            ) : null}
                            <ImageUpload
                                onChange={(files: any) => setImageFile(files?.length ? files[0] : null)}
                                allow="image/*"
                            />
                        </Stack>
                        <Stack spacing={1} sx={{ flex: 1 }}>
                            <Typography variant="subtitle2">Cover Image</Typography>
                            {existingCoverImage ? (
                                <CustomImage
                                    src={existingCoverImage}
                                    style={{ width: 160, height: 96, objectFit: 'cover', borderRadius: 8 }}
                                />
                            ) : null}
                            <ImageUpload
                                onChange={(files: any) => setCoverImageFile(files?.length ? files[0] : null)}
                                allow="image/*"
                            />
                        </Stack>
                    </Stack>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button onClick={handleSubmit} variant="contained" disabled={saving}>
                    {saving ? 'Saving...' : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EditExternalCatalogDialog;
